import React from 'react';
import { Grid } from '@mui/material';
import { Nullable } from '@jamesgmarks/utilities';
import { IBaseSubscriptions } from '@llws/lift-entity-interfaces';
import { IWhereCommand, TWhereCondition } from "@llws/dynamic-router-utils";
import { ClientDropdown } from 'src/components/parts/ClientDropdown';
import { BaseSubscriptionDropdown } from "src/components/parts/BaseSubscriptionDropdown";
import { ISubscriptionData } from '../types';
import { ICommissionSchemeData } from '../../EditorComponents/CommissionEditor/CommissionEditor';
import { makeYmd } from 'src/app-utils';

interface ISubscriptionBaseDetailsProps {
  subscriptionData: ISubscriptionData;
  setSubscriptionData: React.Dispatch<React.SetStateAction<ISubscriptionData>>;
  setSelectedBaseSubscription: React.Dispatch<React.SetStateAction<Nullable<IBaseSubscriptions>>>;
  setSelectedClientSalesRepId: React.Dispatch<React.SetStateAction<Nullable<number>>>;
  setCommissionSchemeData: React.Dispatch<React.SetStateAction<ICommissionSchemeData>>;
  baseSubscriptionQueryFilters: IWhereCommand[] | { field: string; value: string; condition: TWhereCondition }[];
  isCreateMode: boolean;
}

export const SubscriptionBaseDetails: React.FC<ISubscriptionBaseDetailsProps> = ({
  subscriptionData,
  setSubscriptionData,
  setSelectedBaseSubscription,
  setSelectedClientSalesRepId,
  setCommissionSchemeData,
  baseSubscriptionQueryFilters,
  isCreateMode,
}) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <ClientDropdown
          clientId={subscriptionData.clientId ?? null}
          onClientChanged={(client) => {
            if (!client) {
              setSubscriptionData((old) => ({
                ...old,
                clientId: null,
                clientFreshbooksId: null,
              }));

              setSelectedClientSalesRepId(null);
            } else if (client.id !== subscriptionData.clientId) {
              setSubscriptionData((old) => ({
                ...old,
                clientId: client.id,
                clientFreshbooksId: client.freshbooksClientId,
              }));

              setSelectedClientSalesRepId(client.salesRepId);

              if (client.salesRepId) {
                setCommissionSchemeData((old) => ({ ...old, salesRepId: client.salesRepId }));
              }
            }
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <BaseSubscriptionDropdown
          label='Base subscription'
          baseSubscriptionId={subscriptionData.baseSubscriptionId ?? null}
          queryFilters={baseSubscriptionQueryFilters}
          onBaseSubscriptionChanged={(baseSubscription) => {
            const config = (
              baseSubscription?.customConfiguration ?? null);

            if (!baseSubscription) {
              setSelectedBaseSubscription(null);
              setSubscriptionData((old) => ({
                ...old,
                baseSubscriptionId: null,
                baseSubscriptionConfiguration: null,
                invoiceDescription: '',
                billingTypeId: 0,
              }));

              return;
            }

            setSubscriptionData((old) => ({
              ...old,
              baseSubscriptionId: baseSubscription.id ?? null,
              baseSubscriptionConfiguration: config,
              invoiceDescription: baseSubscription.invoiceDescription,
              billingTypeId: baseSubscription.service?.billingTypeId ?? 0,
              revenueTrackingCode: (
                baseSubscription.revenueTrackingCode ?? baseSubscription.service?.revenueTrackingCode ?? ''
              ),
              startDate: isCreateMode ? makeYmd(new Date(), true) : old.startDate,
            }));

            setSelectedBaseSubscription(baseSubscription);
          }}
        />
      </Grid>
    </Grid>
  );
};
